import { ChangeEvent, useCallback, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Banner } from '@cbhq/cds-web/alpha/banner/Banner';
import { Button } from '@cbhq/cds-web/buttons';
import { NativeTextArea, Select, SelectOption, TextInput } from '@cbhq/cds-web/controls';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { Link, TextLabel2 } from '@cbhq/cds-web/typography';

import useJSONValidation from ':cloud/hooks/useJSONValidation';
import { GetSmartContractsResponse } from ':cloud/types/service_proto';
import { CoinbaseSmartContract } from ':cloud/types/ts_types';

import { getNetwork, networksMap, validateEthereumAddress } from '../helpers';

type SmartContractRegisterModalProps = {
  onRequestClose?: () => void;
  onEditPress: (smartContract: CoinbaseSmartContract | null) => void;
  onSubmit: (smartContract: CoinbaseSmartContract) => void;
  smartContractsData?: GetSmartContractsResponse;
};

export function CoinbaseSmartContractRegisterModal({
  onRequestClose,
  onSubmit,
  onEditPress,
  smartContractsData,
}: Readonly<SmartContractRegisterModalProps>) {
  const networks = Object.keys(networksMap);

  const [contractName, setContractName] = useState('');
  const [contractAddress, setContractAddress] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [contractAddressError, setContractAddressError] = useState('');
  const [contractAbi, setContractAbi] = useState('');
  const [duplicatedContract, setDuplicatedContract] = useState<CoinbaseSmartContract | null>(null);
  const {
    validateJSON,
    jsonValidationError: contractAbiError,
    clearJSONValidationError,
  } = useJSONValidation();
  const { isPhone } = useBreakpoints();

  const getContractFromList = useCallback(
    (address: string, network: string) => {
      const data = smartContractsData?.data ?? [];
      for (const contract of data) {
        if (contract.contractAddress === address && contract.networkId === network) {
          return contract;
        }
      }
      return null;
    },
    [smartContractsData],
  );

  const handleOnChangeContractName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setContractName(name);
  }, []);

  const handleNetworkChange = useCallback(
    (network: string) => {
      const duplicated = getContractFromList(contractAddress, network);
      setDuplicatedContract(duplicated);
      setSelectedNetwork(network);
    },
    [getContractFromList, contractAddress],
  );

  const validateContractAddress = useCallback(() => {
    if (contractAddress && !validateEthereumAddress(contractAddress)) {
      setContractAddressError(
        'Please enter a valid Ethereum address (40 hexadecimal characters starting with 0x)',
      );
    } else {
      setContractAddressError('');
    }
  }, [contractAddress, setContractAddressError]);

  const handleOnChangeContractAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const address = event.target.value.trim();
      const duplicated = getContractFromList(address, selectedNetwork);

      setDuplicatedContract(duplicated);
      setContractAddress(address);

      if (contractAddressError) {
        setContractAddressError('');
      }
    },
    [contractAddressError, getContractFromList, selectedNetwork],
  );

  const handleOnBlurContractAddress = useCallback(() => {
    validateContractAddress();
  }, [validateContractAddress]);

  const handleOnChangeContractAbi = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const newContractAbi = event.target.value.trim();
      setContractAbi(newContractAbi);

      if (contractAbiError) {
        clearJSONValidationError();
      }
    },
    [clearJSONValidationError, contractAbiError],
  );

  const handleOnBlurContractAbi = useCallback(() => {
    validateJSON(contractAbi);
  }, [validateJSON, contractAbi]);

  const submit = useCallback(() => {
    const smartContract: CoinbaseSmartContract = {
      contractName: contractName.trim(),
      contractAddress,
      networkId: selectedNetwork,
      abi: contractAbi,
    };
    onSubmit(smartContract);
  }, [contractAbi, contractAddress, contractName, onSubmit, selectedNetwork]);

  const handleSubmit = useCallback(() => {
    validateJSON(contractAbi);
    validateContractAddress();

    if (contractAbiError || contractAddressError || duplicatedContract) {
      return;
    }

    submit();
  }, [
    validateJSON,
    contractAbi,
    contractAbiError,
    submit,
    contractAddressError,
    validateContractAddress,
    duplicatedContract,
  ]);

  const handleOnClickEdit = () => {
    onEditPress(duplicatedContract);
  };

  return (
    <Modal onRequestClose={onRequestClose ?? noop} visible width={isPhone ? undefined : 480}>
      <ModalHeader title="Register Smart Contract" />
      <ModalBody>
        <VStack gap={2}>
          <TextInput
            label="Name"
            required
            placeholder="Contract name"
            value={contractName}
            onChange={handleOnChangeContractName}
          />
          <TextInput
            label="Contract Address"
            placeholder="0xdeadbeef"
            value={contractAddress}
            onChange={handleOnChangeContractAddress}
            onBlur={handleOnBlurContractAddress}
            helperText={contractAddressError}
            variant={contractAddressError ? 'negative' : undefined}
          />

          <Select
            value={getNetwork(selectedNetwork)}
            label="Network"
            placeholder="Select Network"
            onChange={handleNetworkChange}
          >
            {networks.map((option) => (
              <SelectOption value={option} key={option} title={getNetwork(option)} />
            ))}
          </Select>
          {duplicatedContract && (
            <Banner
              startIcon="warning"
              styleVariant="contextual"
              title="The smart contract of the given contract address and network has already been registered"
              variant="warning"
              primaryAction={<Link onClick={handleOnClickEdit}>Go to edit</Link>}
            >
              <TextLabel2 as="p">
                You can change the uploaded ABI by going to the edit page.
              </TextLabel2>
            </Banner>
          )}
          <TextInput
            inputNode={
              <NativeTextArea
                value={contractAbi}
                onChange={handleOnChangeContractAbi}
                rows={7}
                cols={5}
                placeholder="Enter the smart contract ABI here"
              />
            }
            helperText={contractAbiError}
            onBlur={handleOnBlurContractAbi}
            label="Contract ABI"
            variant={contractAbiError ? 'negative' : undefined}
          />
        </VStack>
      </ModalBody>
      <ModalFooter
        secondaryAction={
          <Button variant="secondary" onPress={onRequestClose}>
            Cancel
          </Button>
        }
        primaryAction={
          <Button
            type="submit"
            onPress={handleSubmit}
            testID="submit"
            disabled={
              !contractName ||
              !contractAddress ||
              !selectedNetwork ||
              !contractAbi ||
              Boolean(duplicatedContract)
            }
          >
            Register
          </Button>
        }
      />
    </Modal>
  );
}
